import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Send, Sparkles, CheckCircle2, ArrowRight, RotateCcw, TrendingUp, Trophy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TimeVolumeCard } from "@/components/TimeVolumeCard";
import { useFeatures } from "@/lib/features";

const MODE_CARDS = [
  { key: "reprendre", Icon: RotateCcw, title: "Je veux reprendre la course", sub: "Reprendre progressivement et retrouver de la régularité." },
  { key: "progresser", Icon: TrendingUp, title: "Je veux courir et progresser", sub: "Un entraînement évolutif, sans compétition obligatoire." },
  { key: "course", Icon: Trophy, title: "Je prépare une course", sub: "Une préparation structurée jusqu'à une date précise." },
];

export default function Bilan() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [synthesis, setSynthesis] = useState(null);
  const [modeStep, setModeStep] = useState(null); // null=chargement, true=choix à faire, false=fait
  const [availStep, setAvailStep] = useState(false); // étape saisie disponibilité (onboarding)
  const [choice, setChoice] = useState(null);
  const [race, setRace] = useState({ race_name: "", race_distance: "semi", race_date: "", target_time: "", target_dplus: "", distance_km: "" });
  const [savingMode, setSavingMode] = useState(false);
  const [startFrom, setStartFrom] = useState(null); // prix « à partir de » dynamique (min des offres)
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const features = useFeatures();
  // Cache/état : neutralise un ancien objectif « trail » quand la feature est en sommeil.
  useEffect(() => {
    if (!features.trail_enabled && race.race_distance === "trail") {
      setRace((r) => ({ ...r, race_distance: "semi", target_dplus: "", distance_km: "" }));
    }
  }, [features, race.race_distance]);

  const startChat = () => api.post("/bilan/start").then((res) => setMessages(res.data.messages || []));

  useEffect(() => {
    api.get("/plans").then((r) => {
      const prices = (r.data.plans || []).map((p) => p.promo_price || p.price).filter((x) => typeof x === "number");
      if (prices.length) setStartFrom(Math.min(...prices));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/coaching-mode")
      .then((r) => {
        if (r.data?.explicit) { setModeStep(false); startChat(); }
        else setModeStep(true);
      })
      .catch(() => { setModeStep(false); startChat(); });
  }, []);

  const submitMode = async () => {
    if (!choice) return;
    if (choice === "course" && (!race.race_date || !race.race_distance)) {
      toast.error("Indique au moins la distance et la date de ta course.");
      return;
    }
    setSavingMode(true);
    try {
      const payload = choice === "course"
        ? { choice, race_name: race.race_name || "Ma course", race_distance: race.race_distance, race_date: race.race_date, target_time: race.target_time,
            target_dplus: race.race_distance === "trail" && race.target_dplus ? parseInt(race.target_dplus, 10) : null,
            distance_km: race.race_distance === "trail" && race.distance_km ? parseFloat(race.distance_km) : null }
        : { choice };
      await api.post("/coaching-mode", payload);
      setModeStep(false);
      setAvailStep(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSavingMode(false);
    }
  };


  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/bilan/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch {
      toast.error("Erreur de connexion à l'IA");
    } finally {
      setStreaming(false);
    }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const res = await api.post("/bilan/finish");
      setSynthesis(res.data.synthesis);
    } catch {
      toast.error("Impossible de générer la synthèse");
    } finally {
      setFinishing(false);
    }
  };

  if (synthesis) {
    return (
      <div className="min-h-screen bg-sand">
        <div className="max-w-2xl mx-auto px-5 py-12 fade-up">
          <ProvenanceBadge type="analyse">ANALYSE IA · TON BILAN</ProvenanceBadge>
          <h1 className="mt-6 text-3xl sm:text-4xl font-light tracking-tight">Ton profil sportif</h1>
          <p className="mt-4 text-obsidian/70 leading-relaxed">{synthesis.resume}</p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <Block title="Points forts" items={synthesis.points_forts} />
            <Block title="Points de vigilance" items={synthesis.points_vigilance} />
          </div>
          <div className="mt-4">
            <Block title="Grandes orientations possibles" items={synthesis.orientations} />
          </div>
          {synthesis.disclaimer && (
            <p className="mt-6 text-xs text-obsidian/50 border-l-2 border-sage pl-3">{synthesis.disclaimer}</p>
          )}

          <div className="fade-up-2 mt-10 rounded-[28px] bg-obsidian text-white p-8 shadow-premium-hover">
            <h3 className="text-xl font-medium">Passe à l'accompagnement</h3>
            <p className="mt-2 text-white/70 text-sm leading-relaxed">
              Programmation personnalisée, adaptations continues et chat IA illimité — avec ou sans suivi humain.
            </p>
            <div className="mt-6 flex items-center justify-between">
              <span className="font-display text-2xl font-light">Dès {startFrom ?? 29} €<span className="text-base text-white/50"> / cycle</span></span>
              <button data-testid="goto-pricing-button" onClick={() => navigate("/tarifs")}
                className="inline-flex items-center gap-2 rounded-full bg-white text-obsidian px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform">
                Voir les offres <ArrowRight size={16} />
              </button>
            </div>
          </div>
          <button onClick={() => navigate("/app")} className="mt-6 text-sm text-obsidian/60 hover:text-obsidian">Plus tard → accéder à mon espace</button>
        </div>
      </div>
    );
  }

  if (modeStep !== false) {
    return (
      <div className="min-h-screen bg-sand">
        <div className="max-w-2xl mx-auto px-5 py-12 sm:py-16 fade-up">
          <ProvenanceBadge type="analyse">BIENVENUE</ProvenanceBadge>
          <h1 className="mt-6 text-3xl sm:text-4xl font-light tracking-tight">Qu'est-ce qui t'amène ?</h1>
          <p className="mt-3 text-obsidian/60 text-sm sm:text-base">On adapte ton coaching à ton besoin. Tu pourras changer plus tard.</p>

          {modeStep === null ? (
            <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-obsidian/40" /></div>
          ) : (
            <>
              <div className="mt-8 space-y-3.5">
                {MODE_CARDS.map(({ key, Icon, title, sub }) => (
                  <button key={key} data-testid={`mode-card-${key}`} onClick={() => setChoice(key)}
                    className={`w-full text-left rounded-2xl border p-5 sm:p-6 flex items-start gap-4 transition-[border-color,background-color,transform] active:scale-[0.99] ${choice === key ? "border-obsidian bg-white shadow-premium" : "border-border bg-white/70 hover:border-obsidian/40"}`}>
                    <span className={`w-11 h-11 shrink-0 rounded-full flex items-center justify-center ${choice === key ? "bg-obsidian text-white" : "bg-sage/12 text-sage"}`}><Icon size={20} /></span>
                    <span className="flex-1">
                      <span className="block text-base sm:text-lg font-medium">{title}</span>
                      <span className="block mt-1 text-sm text-obsidian/55">{sub}</span>
                    </span>
                    {choice === key && <CheckCircle2 size={20} className="text-sage shrink-0 mt-1" />}
                  </button>
                ))}
              </div>

              {choice === "course" && (
                <div data-testid="race-fields" className="mt-5 rounded-2xl border border-border bg-white p-5 sm:p-6 fade-up space-y-3.5">
                  <div className="text-xs sm:text-sm tracking-[0.16em] uppercase font-semibold text-obsidian/50">Ta course</div>
                  <input data-testid="race-name-input" value={race.race_name} onChange={(e) => setRace({ ...race, race_name: e.target.value })}
                    placeholder="Nom de la course (optionnel)" className="w-full rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
                  <div className="grid grid-cols-2 gap-3">
                    <select data-testid="race-distance-input" value={race.race_distance} onChange={(e) => setRace({ ...race, race_distance: e.target.value })}
                      className="rounded-xl border border-border px-4 py-3 text-base bg-white focus:ring-1 focus:ring-obsidian focus:outline-none">
                      <option value="5km">5 km</option>
                      <option value="10km">10 km</option>
                      <option value="semi">Semi-marathon</option>
                      <option value="marathon">Marathon</option>
                      {features.trail_enabled && <option value="trail">Trail</option>}
                    </select>
                    <input data-testid="race-date-input" type="date" value={race.race_date} onChange={(e) => setRace({ ...race, race_date: e.target.value })}
                      className="rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
                  </div>
                  <input data-testid="race-target-input" value={race.target_time} onChange={(e) => setRace({ ...race, target_time: e.target.value })}
                    placeholder="Chrono visé (optionnel, ex : 1h45)" className="w-full rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
                  {features.trail_enabled && race.race_distance === "trail" && (
                    <div data-testid="race-dplus-field" className="fade-up space-y-3.5">
                      <div className="grid grid-cols-2 gap-3">
                        <input data-testid="race-km-input" type="number" inputMode="decimal" min="0" step="1" value={race.distance_km}
                          onChange={(e) => setRace({ ...race, distance_km: e.target.value })}
                          placeholder="Distance (km, ex : 42)"
                          className="rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
                        <input data-testid="race-dplus-input" type="number" inputMode="numeric" min="0" step="50" value={race.target_dplus}
                          onChange={(e) => setRace({ ...race, target_dplus: e.target.value })}
                          placeholder="D+ (mètres, ex : 1500)"
                          className="rounded-xl border border-border px-4 py-3 text-base focus:ring-1 focus:ring-obsidian focus:outline-none" />
                      </div>
                      <p className="text-xs text-obsidian/50">Distance et dénivelé calibrent ta préparation trail : volume sentier, sortie longue et travail de force proportionnés à ton objectif.</p>
                    </div>
                  )}
                </div>
              )}

              <button data-testid="mode-continue-button" onClick={submitMode} disabled={!choice || savingMode}
                className="mt-8 w-full rounded-full bg-obsidian text-white py-5 text-base font-medium hover:-translate-y-0.5 active:scale-[0.99] transition-transform disabled:opacity-40 inline-flex items-center justify-center gap-2">
                {savingMode ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />} Continuer
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (availStep) {
    return (
      <div className="min-h-screen bg-sand">
        <div className="max-w-2xl mx-auto px-5 py-12 sm:py-16 fade-up" data-testid="onboarding-availability">
          <ProvenanceBadge type="analyse">TES DISPONIBILITÉS</ProvenanceBadge>
          <h1 className="mt-6 text-3xl sm:text-4xl font-light tracking-tight">Combien de temps pour courir ?</h1>
          <p className="mt-3 text-obsidian/60 text-sm sm:text-base">
            Pas besoin de choisir des jours fixes : indique le nombre de séances et tes durées maximales.
            Ton programme s'y adaptera précisément et restera souple.
          </p>
          <div className="mt-8">
            <TimeVolumeCard onSaved={() => { setAvailStep(false); startChat(); }} />
          </div>
          <button data-testid="avail-skip-button" onClick={() => { setAvailStep(false); startChat(); }}
            className="mt-5 w-full text-sm text-obsidian/60 hover:text-obsidian">
            Passer cette étape pour l'instant →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-sand flex flex-col overflow-hidden h-screen [height:100dvh]">
      <header className="shrink-0 bg-sand/90 backdrop-blur-md border-b border-border">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center"><Sparkles size={15} className="text-sage" /></span>
            <div>
              <div className="text-sm font-medium leading-none">Assistant IA</div>
              <div className="text-[11px] text-obsidian/50 mt-0.5">Bilan sportif</div>
            </div>
          </div>
          <button data-testid="finish-bilan-button" onClick={finish} disabled={finishing || messages.length < 3}
            className="inline-flex items-center gap-1.5 rounded-full border border-obsidian px-4 py-2 text-xs font-semibold disabled:opacity-30 hover:bg-obsidian hover:text-white transition-[background-color,color]">
            <CheckCircle2 size={14} /> {finishing ? "Analyse…" : "Terminer"}
          </button>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        <div className="max-w-2xl mx-auto px-5 py-6 space-y-6">
          {messages.map((m, i) => (
            <div key={i} className="fade-up">
              {m.role === "assistant" ? (
                <div className="flex gap-3">
                  <span className="mt-1 w-7 h-7 shrink-0 rounded-full bg-sage/15 flex items-center justify-center"><Sparkles size={13} className="text-sage" /></span>
                  <div className="rounded-2xl rounded-tl-sm bg-white border border-border px-4 py-3 text-[15px] leading-relaxed text-obsidian/90 shadow-[0_4px_16px_rgba(10,25,47,0.04)]">
                    {m.content || <span className="pulse-organic">…</span>}
                  </div>
                </div>
              ) : (
                <div className="flex justify-end">
                  <div className="rounded-2xl rounded-tr-sm bg-obsidian text-white px-4 py-3 text-[15px] leading-relaxed max-w-[85%]">{m.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="shrink-0 bg-sand/95 backdrop-blur-md border-t border-border pb-[env(safe-area-inset-bottom)]">
        <div className="max-w-2xl mx-auto px-5 pt-3">
          {messages.length >= 3 && (
            <button data-testid="finish-bilan-bottom-button" onClick={finish} disabled={finishing}
              className="w-full mb-3 inline-flex items-center justify-center gap-2 rounded-full bg-obsidian text-white py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              <CheckCircle2 size={15} /> {finishing ? "Analyse en cours…" : "Terminer et accéder à mon bilan"}
            </button>
          )}
        </div>
        <div className="max-w-2xl mx-auto px-5 pb-4 flex items-end gap-3">
          <textarea data-testid="bilan-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Réponds naturellement…"
            className="flex-1 resize-none rounded-2xl border border-border bg-white px-4 py-3 text-[15px] focus:ring-1 focus:ring-obsidian focus:outline-none" />
          <button data-testid="bilan-send-button" onClick={send} disabled={streaming || !input.trim()}
            className="w-11 h-11 shrink-0 rounded-full bg-obsidian text-white flex items-center justify-center disabled:opacity-30 hover:-translate-y-0.5 transition-transform">
            <Send size={17} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Block({ title, items }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5 shadow-premium">
      <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50">{title}</div>
      <ul className="mt-3 space-y-2">
        {(items || []).map((it, i) => (
          <li key={i} className="text-sm text-obsidian/80 flex gap-2"><span className="text-sage mt-1">—</span>{it}</li>
        ))}
        {(!items || items.length === 0) && <li className="text-sm text-obsidian/40">—</li>}
      </ul>
    </div>
  );
}
